import { SnackbarProvider } from 'notistack';

import React from 'react';
import { useSelector } from 'react-redux';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  createRoutesFromChildren,
  createSearchParams,
  matchRoutes,
  useLocation,
  useNavigationType,
  useParams,
} from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';

import {
  ErrorBoundary,
  init,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
  withProfiler,
  withSentryReactRouterV6Routing,
} from '@sentry/react';

import { TrackingPixel, envUtils } from '@ft-frontend/core';

import AuthWrapper from 'components/AuthWrapper';
import ErrorMessage from 'components/ErrorMessage';
import NavigateSqueaky from 'components/NavigateSqueaky';
import Online from 'components/Online';
import OnlineTradeinWrapper from 'components/OnlineTradeinWrapper';
import PosalePageContainer from 'components/PosalePageContainer';
import RoleWrapper from 'components/RoleWrapper';

import { ADMIN, ADMIN_WHITELIST, VENDOR_MANAGER } from 'constants/user';

import OnlineStartingInfoProvider from 'contexts/starting_info/OnlineStartingInfoProvider';
import PosaleStartingInfoProvider from 'contexts/starting_info/PosaleStartingInfoProvider';

const Accessories = React.lazy(() =>
  import('pages/posale/admin/accessories/Accessories'),
);
const Approved = React.lazy(() => import('pages/online/Approved'));
const Campaigns = React.lazy(() =>
  import('pages/posale/admin/campaigns/Campaigns'),
);
const Blocks = React.lazy(() => import('pages/posale/admin/emails/Blocks'));
const BlockForm = React.lazy(() =>
  import('pages/posale/admin/emails/components/BlockForm'),
);
const CampaignDetails = React.lazy(() =>
  import('pages/posale/admin/campaigns/[campaignCode]/details/CampaignDetails'),
);
const CampaignDetailsCohort = React.lazy(() =>
  import(
    'pages/posale/admin/campaigns/[campaignCode]/details/CampaignDetailsCohort'
  ),
);
const CampaignDetailsCommunications = React.lazy(() =>
  import(
    'pages/posale/admin/campaigns/[campaignCode]/details/CampaignDetailsCommunications'
  ),
);
const CampaignDetailsOffers = React.lazy(() =>
  import(
    'pages/posale/admin/campaigns/[campaignCode]/details/CampaignDetailsOffers'
  ),
);

const CampaignDetailsReports = React.lazy(() =>
  import(
    'pages/posale/admin/campaigns/[campaignCode]/details/CampaignDetailsReports'
  ),
);
const Categories = React.lazy(() => import('pages/online/Categories'));
//const Checkout = React.lazy(() => import('pages/posale/checkout/Checkout'));
const Collections = React.lazy(() =>
  import('pages/posale/admin/collections/Collections'),
);
const CollectionDetails = React.lazy(() =>
  import('pages/posale/admin/collections/[collectionUUID]/CollectionDetails'),
);
const CollectionsOrganise = React.lazy(() =>
  import('pages/posale/admin/collections/organise/CollectionsOrganise'),
);

const ContractDateAdjust = React.lazy(() =>
  import('pages/posale/admin/contracts/adjust/ContractDateAdjust'),
);
const ContractSearch = React.lazy(() => import('pages/online/ContractSearch'));
const ContractSummary = React.lazy(() =>
  import('pages/online/ContractSummary'),
);
const CreditCheck = React.lazy(() => import('pages/online/CreditCheck'));
const Customization = React.lazy(() => import('pages/online/Customization'));
const CustomerOptions = React.lazy(() =>
  import('pages/online/CustomerOptions'),
);
const Delivery = React.lazy(() => import('pages/online/Delivery'));
const Devices = React.lazy(() => import('pages/posale/admin/devices/Devices'));
const Evaluate = React.lazy(() => import('pages/online/Evaluate'));
const EstimatedValue = React.lazy(() =>
  import('pages/online/tradein/EstimatedValue'),
);
const Extras = React.lazy(() => import('pages/online/Extras'));
const Families = React.lazy(() => import('pages/online/Families'));
const FindDevice = React.lazy(() => import('pages/online/tradein/FindDevice'));
const Login = React.lazy(() => import('pages/posale/login/Login'));
const OnlineQuoteSearch = React.lazy(() =>
  import('pages/posale/recycle/quotes/search/OnlineQuoteSearch'),
);
const Options = React.lazy(() => import('pages/online/Options'));
const PageNotFound = React.lazy(() => import('pages/PageNotFound'));
const PartialApproved = React.lazy(() =>
  import('pages/online/PartialApproved'),
);
const PasswordChange = React.lazy(() =>
  import('pages/posale/password/change/PasswordChange'),
);
const PasswordForgot = React.lazy(() =>
  import('pages/posale/login/recover/PasswordForgot'),
);
const PasswordReset = React.lazy(() =>
  import('pages/posale/login/resetpassword/[id]/PasswordReset'),
);
const PendingContracts = React.lazy(() =>
  import('pages/posale/sales/contracts/pending/PendingContracts'),
);
const Recycle = React.lazy(() => import('pages/posale/recycle/quotes/Recycle'));
const RecycleComponents = React.lazy(() =>
  import('pages/posale/recycle/Recycle-components'),
);
const RecycleDetails = React.lazy(() =>
  import('pages/posale/recycle/details/[tradecartID]/RecycleDetails'),
);
const RecycleReports = React.lazy(() =>
  import('pages/posale/reports/recycle/RecycleReports'),
);
const Referred = React.lazy(() => import('pages/online/Referred'));
const Refused = React.lazy(() => import('pages/online/Refused'));
const Result = React.lazy(() => import('pages/online/Result'));
const ReturnCustomer = React.lazy(() => import('pages/online/return/Customer'));
const ReturnEvaluation = React.lazy(() =>
  import('pages/online/return/Evaluation'),
);
const ReturnSummary = React.lazy(() => import('pages/online/return/Summary'));
const Review = React.lazy(() => import('pages/online/Review'));
const Rules = React.lazy(() => import('pages/posale/admin/rules/Rules'));
const Sale = React.lazy(() => import('pages/posale/sales/[cartUUID]/Sale'));
const Sales = React.lazy(() => import('pages/posale/sales/Sales'));
const SalesUpload = React.lazy(() =>
  import('pages/posale/sales/upload/SalesUpload'),
);
//const StandardFinancing = React.lazy(() => import('pages/posale/[financeType]/StandardFinancing'),);
//const Services = React.lazy(() => import('pages/posale/services/Services'));
const Settlements = React.lazy(() =>
  import('pages/posale/reports/settlements/Settlements'),
);
const Stores = React.lazy(() => import('pages/posale/admin/stores/Stores'));
const TemplateEditor = React.lazy(() =>
  import(
    'pages/posale/admin/emails/[campaignCode]/[templateName]/TemplateEditor'
  ),
);
const Test = React.lazy(() => import('pages/online/Test'));
const TestContracts = React.lazy(() =>
  import('pages/posale/admin/testcontracts/TestContracts'),
);
const TestContractDetails = React.lazy(() =>
  import(
    'pages/posale/admin/testcontracts/[testContractID]/TestContractDetails'
  ),
);
const TradeInCustomer = React.lazy(() =>
  import('pages/online/tradein/Customer'),
);
const TradeInEvaluation = React.lazy(() =>
  import('pages/online/tradein/Evaluation'),
);
const TradeInSummary = React.lazy(() => import('pages/online/tradein/Summary'));
const Unexpected = React.lazy(() => import('pages/online/Unexpected'));
//const Upgrade = React.lazy(() => import('pages/posale/[financeType]/Upgrade'));
const Users = React.lazy(() => import('pages/posale/admin/users/Users'));
const Vouchers = React.lazy(() =>
  import('pages/posale/admin/vouchers/Vouchers'),
);
const Withdrawn = React.lazy(() => import('pages/online/Withdrawn'));

init({
  autoSessionTracking: true,
  dsn: 'https://6a30b8d17641de97421f038dd7a469c3@o348393.ingest.us.sentry.io/4510313544155137',
  integrations: [
    reactRouterV6BrowserTracingIntegration(
      React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    ),
    replayIntegration({
      maskAllText: process.env.REACT_APP_SENTRY_ENV !== 'development',
      blockAllMedia: true,
      stickySession: true,
      maxReplayDuration: 600000,
    }),
  ],
  debug: process.env.REACT_APP_SENTRY_DEBUG === 'true',
  environment: process.env.REACT_APP_SENTRY_ENV || 'development',
  release: process.env.REACT_APP_SENTRY_RELEASE,
  sampleRate: process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.5, // error events
  tracesSampleRate:
    process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.2, // performance traces
  replaysSessionSampleRate: 0, // all sessions
  replaysOnErrorSampleRate:
    process.env.REACT_APP_SENTRY_ENV === 'development' ? 1.0 : 0.2, // sessions with errors
  normalizeDepth: process.env.REACT_APP_SENTRY_ENV === 'development' ? 10 : 3, // state levels
  normalizeMaxBreadth: 20, // state number of properties
});

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

const App = ({ persistor }) => {
  const snackbarDisplayTime = useSelector(
    (state) => state.ui.snackbarDisplayTime,
  );
  const pageName = useSelector((state) => state.ui.pageName);
  const wlId = useSelector((state) => state.ui.wlId);
  const storeId = useSelector((state) => state.ui.storeId);
  const notistackRef = React.createRef();

  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  // https://stackoverflow.com/questions/70073688/react-router-6-navigate-to-using-params
  const NavigateParamsToQuery = ({ to, replace = true }) => {
    const params = useParams();

    return (
      <Navigate
        to={`${to}?${createSearchParams(params).toString()}`}
        replace={replace}
      />
    );
  };

  const RedirectWithParams = ({ to, ...props }) => {
    const insertParams = (to, params) => {
      const entries = Object.entries(params);

      entries.forEach(([key, value]) => {
        to = to.replace(`:${key}`, `${value}`);
      });

      return to;
    };

    const params = useParams();
    return <Navigate to={insertParams(to, params)} {...props} replace />;
  };

  return (
    <ErrorBoundary
      fallback={({ resetError }) => <ErrorMessage resetError={resetError} />}
    >
      <SnackbarProvider
        autoHideDuration={snackbarDisplayTime || 5000}
        content={(key, messageParam) => {
          const isStringMessage =
            typeof messageParam === 'string' || messageParam instanceof String;

          let variant = 'error';
          let title = '';
          let message = messageParam;

          if (!isStringMessage) {
            ({ variant, title, message } = messageParam);
          }

          return (
            <Alert
              id={key}
              severity={variant}
              variant='filled'
              action={
                <IconButton
                  color='inherit'
                  sx={{ p: 0.5 }}
                  onClick={onClickDismiss(key)}
                >
                  <CloseIcon />
                </IconButton>
              }
            >
              {title && <AlertTitle>{title}</AlertTitle>}
              {message}
              <TrackingPixel
                type='errortrack'
                pageName={pageName}
                display={variant}
                wlID={wlId}
                storeID={storeId}
                message={message}
              />
            </Alert>
          );
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        ref={notistackRef}
      >
        <BrowserRouter>
          <SentryRoutes>
            <Route index element={<Navigate to='online' replace={true} />} />
            <Route path='online/tradein' element={<OnlineTradeinWrapper />}>
              <Route
                path='search'
                element={<Navigate to='/online/tradein/finddevice' replace />}
              />
              <Route
                exact
                path='finddevice'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <FindDevice />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='estimation'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <EstimatedValue />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='delivery'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <TradeInCustomer />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='summary'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <TradeInSummary />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='evaluation'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <TradeInEvaluation />
                  </React.Suspense>
                }
              />
            </Route>
            <Route
              exact
              path='online/:campaignID/trade/:tradecartUUID'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <OnlineStartingInfoProvider>
                    <CustomerOptions />
                  </OnlineStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route
              exact
              path='online/:campaignID/order/:orderGUID'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <OnlineStartingInfoProvider>
                    <CustomerOptions />
                  </OnlineStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route
              exact
              path='online/:campaignID/order/:templateName/:orderGUID'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <OnlineStartingInfoProvider>
                    <CustomerOptions />
                  </OnlineStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route path='online' element={<Online />}>
              {!envUtils.isProdEnv() && !envUtils.isDemoEnv() ? (
                <Route index element={<Navigate to='test' replace={true} />} />
              ) : (
                <Route
                  index
                  element={<Navigate to='categories' replace={true} />}
                />
              )}

              <Route
                exact
                path='contract/search'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <ContractSearch />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path=':uuid/summary'
                element={
                  <RedirectWithParams to={`/online/contract/summary/:uuid`} />
                }
              />
              <Route
                exact
                path='contract/summary/:uuid'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <ContractSummary />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='return/evaluation'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <ReturnEvaluation />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='return/delivery'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <ReturnCustomer />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='return/summary'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <ReturnSummary />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='categories'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Categories />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='families/category/:category/:paymentPlanType/:paymentPlanMonths'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Families />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='families/category/:category/:paymentPlanType'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Families />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='options/:deviceCode'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Options />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='customize/:parentDeviceCode/:deviceCode'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Customization />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='extras'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Extras />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='evaluate'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Evaluate />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='delivery'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Delivery />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='review'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Review />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='express/review/:cartGUID'
                element={<NavigateParamsToQuery to='/online/review' />}
              />

              <Route
                exact
                path='creditcheck'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <CreditCheck />
                  </React.Suspense>
                }
              />

              <Route
                exact
                path='result/:cart'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Result />
                  </React.Suspense>
                }
              >
                <Route
                  exact
                  path='approved'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Approved />
                    </React.Suspense>
                  }
                />

                <Route
                  exact
                  path='preapproved'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <PartialApproved />
                    </React.Suspense>
                  }
                />

                <Route
                  exact
                  path='referred'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Referred />
                    </React.Suspense>
                  }
                />

                <Route
                  exact
                  path='refused'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Refused />
                    </React.Suspense>
                  }
                />

                <Route
                  exact
                  path='withdrawn'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Withdrawn />
                    </React.Suspense>
                  }
                />

                <Route
                  exact
                  path='unexpected/:status'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Unexpected />
                    </React.Suspense>
                  }
                />
              </Route>

              {!envUtils.isProdEnv() && !envUtils.isDemoEnv() && (
                <Route
                  exact
                  path='test'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <Test />
                    </React.Suspense>
                  }
                />
              )}

              <Route
                path='*'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PageNotFound />
                  </React.Suspense>
                }
              />
            </Route>
            <Route
              path='posale/login'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <PosaleStartingInfoProvider>
                    <Login />
                  </PosaleStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route
              path='posale/login/:SSOToken'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <PosaleStartingInfoProvider>
                    <Login />
                  </PosaleStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route
              path='posale/login/recover'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <PosaleStartingInfoProvider>
                    <PasswordForgot />
                  </PosaleStartingInfoProvider>
                </React.Suspense>
              }
            />
            <Route
              path='posale/login/resetpassword/:id'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <PosaleStartingInfoProvider>
                    <PasswordReset />
                  </PosaleStartingInfoProvider>
                </React.Suspense>
              }
            />

            <Route
              path='reports/recycle'
              element={<Navigate to='/posale/reports/recycle' replace={true} />}
            />

            <Route
              path='reports/settlements'
              element={
                <Navigate to='/posale/reports/settlements' replace={true} />
              }
            />

            <Route
              path='posale'
              element={<AuthWrapper persistor={persistor} />}
            >
              <Route index element={<NavigateSqueaky />} />
              <Route index element={<Navigate to='login' replace={true} />} />
              <Route
                exact
                path='password/change'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PasswordChange />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='sales'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Sales />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='sales/upload'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <SalesUpload />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='sales/contracts/pending'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <PendingContracts />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              {/** Deprecated - to remove after migration */}
              <Route
                exact
                path=':cartUUID/contract'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <RedirectWithParams to='/posale/sales/:cartUUID' />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='sales/:cartUUID'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Sale />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='recycle/details/:tradecartID'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <RecycleDetails />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='recycle/quotes'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Recycle />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='recycle/quotes/search'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <OnlineQuoteSearch />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='reports/recycle'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <RecycleReports />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='recycle/recycle-components'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <RecycleComponents />
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='reports/settlements'
                element={
                  <RoleWrapper allowedRoles={[ADMIN, VENDOR_MANAGER]}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Settlements />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/rules'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Rules ruleTableIdentifier='RESELLER_MARGIN_RULES' />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/devices'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Devices />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/stores'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Stores />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/users'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Users />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/accessories'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Accessories />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/collections'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Collections />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='admin/collections/:collectionUUID'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <CollectionDetails />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='admin/collections/organise'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <CollectionsOrganise />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='admin/vouchers'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Vouchers />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/contracts/adjust'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <ContractDateAdjust />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/campaigns'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Campaigns />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                path='admin/campaigns/:campaignCode/details'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <CampaignDetails />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              >
                <Route
                  path='reports'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <CampaignDetailsReports />
                    </React.Suspense>
                  }
                />
                <Route
                  path='cohort'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <CampaignDetailsCohort />
                    </React.Suspense>
                  }
                />
                <Route
                  path='offers'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <CampaignDetailsOffers />
                    </React.Suspense>
                  }
                />
                <Route
                  path='communications'
                  element={
                    <React.Suspense fallback={<React.Fragment />}>
                      <CampaignDetailsCommunications />
                    </React.Suspense>
                  }
                />
              </Route>
              {/*<Route
                path='services'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Services />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              />
               <Route
                exact
                path='standard'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <StandardFinancing />
                  </React.Suspense>
                }
              />
              <Route
                exact
                path='upgrade'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <Upgrade />
                  </React.Suspense>
                }
              />
              <Route
                path='checkout'
                element={
                  <React.Suspense fallback={<React.Fragment />}>
                    <PosalePageContainer>
                      <Checkout />
                    </PosalePageContainer>
                  </React.Suspense>
                }
              /> */}
              <Route
                exact
                path='admin/emails/:campaignCode/:templateName'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <TemplateEditor />
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/emails/blocks'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <Blocks />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/emails/blocks/:blockUUID'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <BlockForm />
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/testcontracts'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <TestContracts />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                exact
                path='admin/testcontracts/:testContractID'
                element={
                  <RoleWrapper allowedRoles={ADMIN_WHITELIST}>
                    <React.Suspense fallback={<React.Fragment />}>
                      <PosalePageContainer>
                        <TestContractDetails />
                      </PosalePageContainer>
                    </React.Suspense>
                  </RoleWrapper>
                }
              />
              <Route
                path='*'
                element={
                  <PosalePageContainer>
                    <PageNotFound />
                  </PosalePageContainer>
                }
              />
            </Route>
            <Route
              path='*'
              element={
                <React.Suspense fallback={<React.Fragment />}>
                  <PosalePageContainer>
                    <PageNotFound />
                  </PosalePageContainer>
                </React.Suspense>
              }
            />
          </SentryRoutes>
        </BrowserRouter>
      </SnackbarProvider>
    </ErrorBoundary>
  );
};

export default withProfiler(App);
